import { gql, useQuery } from '@apollo/client';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardSection,
  CardSectionDescription,
  CardSectionTitle,
  CardTitle,
  Link,
  Text,
} from '@apollo/orbit';
import { Restricted } from '@apollo/space-kit/illustrations/Restricted';
import { Satellite2 } from '@apollo/space-kit/illustrations/Satellite2';
import React from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ContactSupportButton } from 'src/components/ContactSupportCTAs';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { isContractDisabled } from '../graphSettingsPage/variantsPage/SourceVariantsPage';
import { useGraphRef } from '../hooks/useGraphRef';
import { contractsConfigRoute, variantSettingsRouteConfig } from '../routes';

import { ContractVariantsTable } from './contractVariants/ContractVariantsTable';

const ContractVariantFragment = gql`
  fragment ContractVariantFragment on GraphVariant {
    id
    name
    isContract
    derivedVariantCount
    sourceVariant {
      id
      name
      supportedDirectives {
        enabled
        name
      }
    }
    supportedDirectives {
      enabled
      name
    }
  }
`;

const VariantContractsSettingsQuery = gql`
  query VariantSettings__ContractsPage($graphId: ID!, $graphVariant: String!) {
    graph: service(id: $graphId) {
      id
      registrySubscriptionsEnabled
      roles {
        canManageVariants
      }
      variant(name: $graphVariant) {
        id
        hasManagedSubgraphs
        activeSchemaPublish {
          id
          createdAt
        }
        ...ContractVariantFragment
      }
      variants {
        id
        isPublic
        isProtected
        hasManagedSubgraphs
        ...ContractVariantFragment
        activeSchemaPublish {
          id
          schema {
            hash
            createdAt
            typeCount
          }
        }
        contractFilterConfig {
          include
          exclude
        }
      }
    }
  }
  ${ContractVariantFragment}
`;

export const VariantContractsSettingsPage = () => {
  const graphRef = useGraphRef();
  const location = useLocation();
  const trackCustomEvent = useTrackCustomEvent();

  const { data, loading, error } = useQuery<
    GraphQLTypes.VariantSettings__ContractsPage,
    GraphQLTypes.VariantSettings__ContractsPageVariables
  >(VariantContractsSettingsQuery, {
    variables: {
      graphId: graphRef?.graphId || '',
      graphVariant: graphRef?.graphVariant || '',
    },
  });
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  if (loading || currentPlan.loading) return <Loading />;
  if ((error || currentPlan.error) && !data)
    return (
      <ErrorMessage className="mb-10" error={error ?? currentPlan.error} />
    );
  if (!data?.graph || !data?.graph.variant) return <NotFound />;

  const contractsEnabled = !!currentPlan?.currentPlan?.contracts;
  const {
    isDisabled: isDisabledOnVariant,
    disabledReason: disabledReasonOnVariant,
  } = isContractDisabled(
    contractsEnabled,
    data.graph.variant?.supportedDirectives || [],
  );

  const canManageVariants = data.graph.roles?.canManageVariants;
  const hasManagedSubgraphs = data.graph.variant.hasManagedSubgraphs;

  return (
    <>
      {data.graph.variant?.isContract ? (
        <div className="flex size-full items-center justify-center py-20">
          <section className="text-center">
            <Satellite2 />
            <Text className="my-2 max-w-120 text-center">
              This is a contract variant, derived from the{' '}
              <b>{data.graph.variant.sourceVariant?.name}</b> source variant.
            </Text>
            <div className="mb-6 max-w-120 text-center">
              <Link
                to={variantSettingsRouteConfig.path({
                  graphId: data.graph.id,
                  graphVariant:
                    data.graph.variant.sourceVariant?.name ||
                    Config.service.defaultVariant,
                  tab: 'contracts',
                })}
              >
                View <b>{data.graph.variant.sourceVariant?.name}</b>'s contract
                variants
                <IconArrowRight className="ml-1" />
              </Link>
            </div>
            <div className="flex justify-center gap-2">
              <Button
                variant="secondary"
                hidden={!canManageVariants}
                as={ReactRouterLink}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.editContractConfigWizard,
                  additionalSearchParameters: {
                    [Config.queryParameters.ContractVariant]:
                      graphRef?.graphVariant,
                    [Config.queryParameters.SourceVariant]:
                      data.graph.variant?.sourceVariant?.name,
                  },
                })}
              >
                Edit contract
              </Button>
              <Button
                variant="secondary"
                hidden={!canManageVariants}
                as={ReactRouterLink}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.configureContractWizard,
                })}
              >
                Create another contract
              </Button>
            </div>
          </section>
        </div>
      ) : !contractsEnabled ? (
        <div
          className="flex size-full items-center justify-center py-20 text-center"
          data-analytics-category="Contracts"
          data-analytics-label="Settings: CTA: non-enterprise"
        >
          <div>
            <Restricted />
            <div className="my-2 max-w-120 text-center text-lg">
              This is an Enterprise feature
            </div>
            <ContactSupportButton
              variant="secondary"
              className="mt-4"
              onClick={() => {
                trackCustomEvent({
                  action: 'contact_us',
                  category: 'Contracts',
                  label: 'Settings: CTA: non-enterprise',
                });
              }}
            >
              Contact us
            </ContactSupportButton>
          </div>
        </div>
      ) : !hasManagedSubgraphs ? (
        <div
          className="flex size-full items-center justify-center py-20 text-center"
          data-analytics-category="Contracts"
          data-analytics-label="Settings: CTA: non-federated"
        >
          <div>
            <Restricted />
            <div className="my-2 max-w-120 text-center text-lg">
              Contracts are only supported for federated{' '}
              <Link
                to="https://www.apollographql.com/docs/federation/"
                isExternal
              >
                variants
              </Link>
              .
            </div>
            <ContactSupportButton
              variant="secondary"
              className="mt-4"
              onClick={() => {
                trackCustomEvent({
                  action: 'contact_us',
                  category: 'Contracts',
                  label: 'Settings: CTA: non-federated',
                });
              }}
            >
              Contact us
            </ContactSupportButton>
          </div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {data.graph.variant.derivedVariantCount}{' '}
              {format.pluralize(
                data.graph.variant.derivedVariantCount,
                'contract',
              )}
            </CardTitle>
            <CardDescription>
              Contracts are filtered views on a source variant's schema that let
              you deliver subsets of your graph to different consumers.
              Contracts are automatically rebuilt every time you push a new
              schema to your source variant.{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://apollographql.com/docs/studio/contracts"
              >
                Learn more
              </Link>
            </CardDescription>
            <Tooltip
              disabled={canManageVariants && !isDisabledOnVariant}
              label={
                !canManageVariants
                  ? 'You do not have permission to create contracts'
                  : disabledReasonOnVariant
              }
            >
              <Button
                variant="secondary"
                isDisabled={isDisabledOnVariant || !canManageVariants}
                as={ReactRouterLink}
                to={contractsConfigRoute.locationFrom({
                  location,
                  patch: {
                    overlay: Config.modals.configureContractWizard,
                    sourceVariant: data.graph.variant?.name,
                  },
                  fromRouteConfig: variantSettingsRouteConfig,
                })}
                className="min-w-auto"
              >
                Create contract
              </Button>
            </Tooltip>
          </CardHeader>
          <CardBody>
            {data.graph.variant.activeSchemaPublish && (
              <CardSection className="pl-[--card-body-padding-x]">
                <CardSectionTitle>Last updated</CardSectionTitle>
                <CardSectionDescription>
                  <LocalTimestamp
                    value={data.graph.variant.activeSchemaPublish.createdAt}
                  />
                </CardSectionDescription>
              </CardSection>
            )}
            <ContractVariantsTable
              graph={data.graph}
              contractsEnabled={currentPlan.currentPlan?.contracts || false}
            />
          </CardBody>
        </Card>
      )}
    </>
  );
};
